import { useState } from "react";
import {
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Alert,
  Chip,
  Box,
} from "@mui/material";
import Dialog from "./dialogs/Dialog";
import { LoadingButton } from "@mui/lab";
import { Upload, AutoFixHigh, Person } from "@mui/icons-material";
import SecondaryButton from "./buttons/SecondaryButton";
import { HostConflict } from "src/mutations/useAssignFollowUpTemplate";
import { useAssignHost } from "src/mutations/useAssignHost";
import { useAssignHostsCSV } from "src/mutations/useAssignHostsCSV";
import { useAssignHostsRoundRobin } from "src/mutations/useAssignHostsRoundRobin";
import { HostSelectInput, MeetingHost } from "./inputs/HostSelectInput";

interface HostConflictResolutionProps {
  open: boolean;
  onClose: () => void;
  eventId: number;
  conflicts: HostConflict[];
  onResolved: () => void;
}

export function HostConflictResolution({
  open,
  onClose,
  eventId,
  conflicts,
  onResolved,
}: HostConflictResolutionProps) {
  const [resolutionMethod, setResolutionMethod] = useState<
    "manual" | "csv" | "round-robin"
  >("manual");
  const [manualAssignments, setManualAssignments] = useState<
    Record<number, MeetingHost>
  >({});
  const [csvData, setCsvData] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assignHost = useAssignHost();
  const assignHostsCSV = useAssignHostsCSV();
  const assignHostsRoundRobin = useAssignHostsRoundRobin();

  const handleManualAssignment = (contactId: number, host: MeetingHost) => {
    setManualAssignments((prev) => ({
      ...prev,
      [contactId]: host,
    }));
  };

  const handleResolveConflicts = async () => {
    setLoading(true);
    setError(null);

    try {
      if (resolutionMethod === "manual") {
        // Assign hosts manually for each conflict
        for (const conflict of conflicts) {
          const host = manualAssignments[conflict.contactId];
          if (host) {
            await assignHost({
              eventId,
              contactId: conflict.contactId,
              hostId: host.id,
            });
          }
        }
      } else if (resolutionMethod === "csv") {
        await assignHostsCSV({
          eventId,
          csvData,
        });
      } else if (resolutionMethod === "round-robin") {
        await assignHostsRoundRobin({
          eventId,
          contactIds: conflicts.map((c) => c.contactId),
        });
      }

      onResolved();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to resolve conflicts",
      );
    } finally {
      setLoading(false);
    }
  };

  const canResolve = () => {
    if (resolutionMethod === "manual") {
      return conflicts.every(
        (conflict) => manualAssignments[conflict.contactId],
      );
    } else if (resolutionMethod === "csv") {
      return csvData.trim().length > 0;
    } else if (resolutionMethod === "round-robin") {
      return true; // Round-robin will use the template's team members
    }
    return false;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      title="Resolve Host Conflicts"
    >
      <DialogContent>
        <Stack spacing={3}>
          <Alert severity="warning">
            {conflicts.length} guest{conflicts.length > 1 ? "s" : ""} could not
            be automatically assigned hosts. Please choose a resolution method
            below.
          </Alert>

          <FormControl fullWidth>
            <InputLabel>Resolution Method</InputLabel>
            <Select
              value={resolutionMethod}
              onChange={(e) => setResolutionMethod(e.target.value as any)}
              label="Resolution Method"
            >
              <MenuItem value="manual">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Person fontSize="small" />
                  <span>Manual Assignment</span>
                </Stack>
              </MenuItem>
              <MenuItem value="csv">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Upload fontSize="small" />
                  <span>CSV Upload</span>
                </Stack>
              </MenuItem>
              <MenuItem value="round-robin">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AutoFixHigh fontSize="small" />
                  <span>Round Robin</span>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>

          {resolutionMethod === "manual" && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">
                Assign hosts manually:
              </Typography>
              {conflicts.map((conflict, index) => (
                <Box
                  key={conflict.key || `${conflict.contactId}-${index}`}
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip
                        label={
                          conflict.contactName
                            ? `${conflict.contactName} (${conflict.contactEmail})`
                            : conflict.contactEmail
                        }
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {conflict.reason}
                      </Typography>
                    </Stack>
                    <HostSelectInput
                      host={manualAssignments[conflict.contactId] || null}
                      onHostSelect={(host) => {
                        if (host) {
                          handleManualAssignment(conflict.contactId, host);
                        }
                      }}
                      forMeetingTransfer={false}
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}

          {resolutionMethod === "csv" && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">
                Upload CSV with guest email and host email pairs:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Format: guestEmail,hostEmail (one pair per line)
              </Typography>
              <TextField
                multiline
                rows={6}
                placeholder="guest1@example.com,host1@example.com&#10;guest2@example.com,host2@example.com"
                value={csvData}
                onChange={(e) => setCsvData(e.target.value)}
                fullWidth
              />
            </Stack>
          )}

          {resolutionMethod === "round-robin" && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">
                Assign hosts using round-robin distribution:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Guests will be automatically assigned to available hosts in
                rotation from the template's team.
              </Typography>
              <Alert severity="info">
                Round-robin assignment will use all active team members from the
                template.
              </Alert>
            </Stack>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <SecondaryButton onClick={onClose} disabled={loading}>
          Cancel
        </SecondaryButton>
        <LoadingButton
          onClick={handleResolveConflicts}
          disabled={!canResolve() || loading}
          loading={loading}
          variant="contained"
          color="primary"
        >
          Resolve Conflicts
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default HostConflictResolution;
